import { useState } from 'react';
import { BlogPost, BlogCategory } from '../../../../types/blog';
import HelpModal, { HelpStep } from '../../../../components/feature/HelpModal';

const BLOG_TABLE_HELP: HelpStep[] = [
  {
    icon: 'ri-list-check',
    title: '記事一覧の見方',
    description: '登録されたブログ記事が一覧で表示されます。サムネイル・タイトル・ステータス・カテゴリー・投稿日を一目で確認できます。',
  },
  {
    icon: 'ri-toggle-line',
    title: 'ステータスの切り替え',
    description: '「公開中」または「下書き」のバッジをクリックするだけで即座に切り替えられます。モーダルを開かずにすばやく変更できます。',
    tips: ['公開中 → クリック → 下書きに切り替わります'],
  },
  {
    icon: 'ri-edit-line',
    title: '記事を編集する',
    description: '「編集」ボタンをクリックすると編集モーダルが開きます。タイトル・本文・カテゴリー・サムネイルなどすべて変更できます。',
  },
  {
    icon: 'ri-delete-bin-line',
    title: '記事を削除する',
    description: '「削除」ボタンをクリックすると確認メッセージが表示されます。「はい」を押すと永久削除されます。',
    tips: ['削除した記事は元に戻せません。慎重に確認してください'],
  },
  {
    icon: 'ri-folder-line',
    title: 'カテゴリー管理',
    description: '「カテゴリー管理」タブに切り替えると、新しいカテゴリーの追加・編集・削除ができます。記事に設定するカテゴリーはここで管理します。',
    tips: ['カテゴリーを先に作ってから記事を追加するとスムーズです'],
  },
];

interface Props {
  posts: BlogPost[];
  categories: BlogCategory[];
  onEdit: (post: BlogPost) => void;
  onDelete: (id: string) => void;
  onStatusToggle: (id: string, current: 'published' | 'draft') => void;
}

export default function AdminBlogTable({ posts, categories, onEdit, onDelete, onStatusToggle }: Props) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const getCategoryById = (id: string | null) =>
    categories.find((c) => c.id === id) ?? null;

  const handleDeleteConfirm = (id: string) => {
    onDelete(id);
    setConfirmId(null);
  };

  if (posts.length === 0) {
    return (
      <div className="text-center py-20" style={{ color: '#9b8b7f' }}>
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ background: '#f2f8f4' }}>
          <i className="ri-article-line text-3xl" style={{ color: '#5a9e6f' }}></i>
        </div>
        <p className="text-lg">記事がありません。新規追加してください。</p>
      </div>
    );
  }

  return (
    <>
      <HelpModal
        title="ブログ記事一覧の使い方"
        sections={[{ label: '使い方', steps: BLOG_TABLE_HELP }]}
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e8d8cc', background: '#fff8f2' }}>
              <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '80px' }}>サムネ</th>
              <th className="py-3 px-4 text-sm font-bold" style={{ color: '#6b5a4e' }}>タイトル</th>
              <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '100px' }}>ステータス</th>
              <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '130px' }}>カテゴリー</th>
              <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '120px' }}>投稿日</th>
              <th className="py-3 px-4 text-sm font-bold whitespace-nowrap" style={{ color: '#6b5a4e', width: '180px' }}>
                <div className="flex items-center gap-2">
                  操作
                  <button
                    onClick={() => setHelpOpen(true)}
                    className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-opacity hover:opacity-70"
                    style={{ background: '#fff0d8', color: '#c4903a', border: '1px solid #f0d8a0' }}
                    title="使い方を見る"
                  >
                    <i className="ri-question-line text-xs"></i>
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => {
              const cat = post.category ?? getCategoryById(post.category_id);
              const isDraft = post.status === 'draft';
              return (
                <tr
                  key={post.id}
                  style={{ borderBottom: '1px solid #f0e8e0', opacity: isDraft ? 0.75 : 1 }}
                  className="hover:bg-[#fffaf6] transition-colors"
                >
                  <td className="py-3 px-4">
                    {post.thumbnail_url ? (
                      <div className="w-16 h-12 rounded overflow-hidden shrink-0">
                        <img src={post.thumbnail_url} alt={post.title} className="w-full h-full object-cover object-center" loading="lazy" />
                      </div>
                    ) : (
                      <div className="w-16 h-12 rounded flex items-center justify-center" style={{ background: '#f0e8e0' }}>
                        <i className="ri-image-line" style={{ color: '#c4b4a8' }}></i>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <p className="line-clamp-2 leading-snug" style={{ color: '#4a3728', fontSize: '14px', fontWeight: '600' }}>{post.title}</p>
                    {post.excerpt && (
                      <p className="line-clamp-1 mt-1" style={{ color: '#9b8b7f', fontSize: '12px' }}>{post.excerpt}</p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => onStatusToggle(post.id, post.status ?? 'published')}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-opacity hover:opacity-75 cursor-pointer whitespace-nowrap"
                      style={isDraft
                        ? { background: '#fdf5e8', color: '#c4903a', border: '1px solid #f0d8a0' }
                        : { background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }
                      }
                      title={isDraft ? 'クリックで公開する' : 'クリックで下書きに戻す'}
                    >
                      <span className="flex items-center justify-center w-3 h-3">
                        <i className={`text-xs ${isDraft ? 'ri-draft-line' : 'ri-global-line'}`}></i>
                      </span>
                      {isDraft ? '下書き' : '公開中'}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    {cat ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap" style={{ background: cat.color, color: '#fff' }}>
                        {cat.name}
                      </span>
                    ) : (
                      <span style={{ color: '#9b8b7f', fontSize: '13px' }}>未設定</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span style={{ color: '#6b5a4e', fontSize: '13px' }}>
                      {post.published_at ? new Date(post.published_at).toLocaleDateString('ja-JP') : '—'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {confirmId === post.id ? (
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#cc4444', fontSize: '12px' }} className="whitespace-nowrap">削除しますか？</span>
                        <button onClick={() => handleDeleteConfirm(post.id)} className="px-2 py-1 rounded text-xs font-bold text-white whitespace-nowrap cursor-pointer" style={{ background: '#cc4444' }}>はい</button>
                        <button onClick={() => setConfirmId(null)} className="px-2 py-1 rounded text-xs font-bold whitespace-nowrap cursor-pointer" style={{ background: '#f0e8e0', color: '#6b5a4e' }}>いいえ</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => onEdit(post)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap" style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}>
                          <span className="flex items-center justify-center w-3 h-3"><i className="ri-edit-line text-xs"></i></span>
                          編集
                        </button>
                        <button onClick={() => setConfirmId(post.id)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap" style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}>
                          <span className="flex items-center justify-center w-3 h-3"><i className="ri-delete-bin-line text-xs"></i></span>
                          削除
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}